import type { IssuedCredentials } from '../credentials/issued-credentials'
import type { ImportIssueRow } from './ImportIssuesTable'
import type { CheckedUsernameRow } from '~/utils/importCsv/username-csv'
import { useMutation } from '@apollo/client/react'
import { Download, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { GQL_IMPORT_USERNAME_LEARNERS } from '~/gql/users/username-learners'
import { userFullName } from '~/helpers/users/display-name'
import { downloadCSV } from '~/utils/exportCsv/utils'
import { parseUsernameCsv, USERNAME_CSV_HEADERS } from '~/utils/importCsv/username-csv'
import ImportIssuesTable from './ImportIssuesTable'

type IProps = {
  /** Comptes créés : leurs identifiants, à remettre ou imprimer tout de suite. */
  onImported: (credentials: IssuedCredentials[]) => void
}

/**
 * Import d'apprenants en pseudo (#271) : un mot de passe généré par ligne,
 * aucun mail. Les lignes refusées restent affichées ; les créées partent dans
 * la fiche d'identifiants.
 */
function UsernameImport({ onImported }: IProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [valid, setValid] = useState<CheckedUsernameRow[]>([])
  const [issues, setIssues] = useState<ImportIssueRow[]>([])
  const [fileName, setFileName] = useState<string | null>(null)
  const [importLearners, { loading }] = useMutation(GQL_IMPORT_USERNAME_LEARNERS)

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Vidé tout de suite : resélectionner le même fichier, corrigé, doit le relire.
    event.target.value = ''
    if (!file) return
    const result = parseUsernameCsv(await file.text())
    if (!result.ok) {
      // L'aperçu d'un fichier précédent ne doit pas rester importable sous ce refus.
      setFileName(null)
      setValid([])
      setIssues([])
      toast.error(t(result.error === 'empty' ? 'import.error.empty' : 'import.username.badHeader'))
      return
    }
    setFileName(file.name)
    setValid(result.valid)
    setIssues(result.issues.map(({ line, username, reason }) => ({ line, value: username, reason })))
  }

  const onImport = async () => {
    try {
      const { data } = await importLearners({ variables: { learners: valid.map(({ row }) => row) } })
      if (!data) return
      const { created, errors } = data.importUsernameLearners
      // Le serveur numérote les lignes qu'il a reçues : remises sur celles du fichier.
      const refused = errors.map(({ line, username, reason }) => ({ line: valid[line - 1]?.line ?? line, value: username, reason }))
      setIssues(current => [...current, ...refused].sort((a, b) => a.line - b.line))
      setValid([])
      const rowOf = new Map(valid.map(({ row }) => [row.username, row]))
      const credentials = created.map(({ username, password }) => {
        const name = userFullName(rowOf.get(username) ?? {})
        return { username, password, ...(name && { name }) }
      })
      if (credentials.length === 0) {
        toast.error(t('bad.import.user.none'))
        return
      }
      if (refused.length > 0) toast.warning(t('warn.import.user.partial', { count: refused.length }))
      onImported(credentials)
    } catch (error) {
      toast.error(t(error instanceof Error && error.message.includes('TOO_MANY_ROWS') ? 'import.username.tooMany' : 'bad.import.user'))
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('import.username.help', { headers: USERNAME_CSV_HEADERS.join(' ; ') })}</p>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
        <Button variant="outline" onClick={() => inputRef.current?.click()}>
          <Upload className="mr-2 size-4" />
          {t('click.to.upload')}
        </Button>
        <Button variant="ghost" onClick={() => downloadCSV('modele-import-pseudos', [...USERNAME_CSV_HEADERS], [['patient-01', 'Ana', 'Silva', 'Éducation thérapeutique', 'CHU', 'Diabétologie']])}>
          <Download className="mr-2 size-4" />
          {t('download.template')}
        </Button>
      </div>
      {fileName && (
        <p className="text-sm font-medium">
          {valid.length > 0 ? t('import.preview.valid', { count: valid.length }) : t('import.preview.none')}
        </p>
      )}
      {issues.length > 0 && <ImportIssuesTable valueLabel={t('common.username')} issues={issues} />}
      {valid.length > 0 && (
        <Button onClick={onImport} disabled={loading}>{t('import.submit', { count: valid.length })}</Button>
      )}
    </div>
  )
}

export default UsernameImport
